import React from "react";
import { formatDate, formatDateTime, formatDateTimeSeconds } from "@/lib/dateFmt";

/**
 * Shared date cell for every transaction / ledger table row.
 * Shows the business (transaction) date and, beneath it, a small line with
 * WHEN the row was entered/edited. v1.31.36 — when the entry was created more
 * than 1 day AFTER its transaction date (backdated), the stamp turns amber and
 * a "BACKDATED" badge + "Entry date" tooltip appear so staff can spot entries
 * added after the fact. Flag only — nothing is blocked.
 */
export default function TxnDateCell({ date, created_at, updated_at, className = "", testId, extra = null }) {
    const wasEdited = !!(updated_at && created_at && updated_at !== created_at);
    const stampTs = wasEdited ? updated_at : (created_at || null);
    const stampLabel = wasEdited ? "Edited" : "Added";
    const stampShort = stampTs ? formatDateTime(stampTs) : "";

    const daysAfter = (() => {
        if (!date || !created_at) return 0;
        try {
            const d1 = new Date(String(date).slice(0, 10));
            const d2 = new Date(String(created_at).slice(0, 10));
            return Math.round((d2 - d1) / 86400000);
        } catch { return 0; }
    })();
    const backdated = daysAfter > 1;

    const tooltip = (() => {
        const lines = [];
        if (backdated) lines.push(`Entry date: ${formatDateTimeSeconds(created_at)}`);
        else if (created_at) lines.push(`Created: ${formatDateTimeSeconds(created_at)}`);
        if (updated_at && wasEdited) lines.push(`Last edited: ${formatDateTimeSeconds(updated_at)}`);
        return lines.join("\n");
    })();

    return (
        <div className={className} data-testid={testId}>
            <div className="whitespace-nowrap">
                {formatDate(date)}
                {backdated && (
                    <span
                        className="ml-1.5 inline-flex items-center rounded-full bg-amber-400 px-1.5 py-0.5 align-middle text-[9px] font-bold uppercase tracking-wider text-amber-950 shadow-sm"
                        title={`Entry date: ${created_at ? formatDate(created_at) : "—"}`}
                        data-testid={testId ? `${testId}-backdated` : undefined}
                    >
                        Backdated
                    </span>
                )}
                {extra}
            </div>
            {stampShort && (
                <div
                    className={`mt-0.5 text-[11px] font-normal leading-tight ${backdated ? "text-amber-700 font-semibold" : "text-slate-400"}`}
                    title={tooltip}
                    data-testid={testId ? `${testId}-stamp` : undefined}
                >
                    {stampLabel} {stampShort}
                </div>
            )}
        </div>
    );
}
